/**
 * Helper functions for working with the Office.js API.
 * https://learn.microsoft.com/en-us/office/dev/add-ins/develop/understanding-the-javascript-api-for-office
 *
 * Do not use Office imports - the library is loaded by external script
 */

import { ROUTES } from '../routes'

function getAsyncResultError() {
  return {
    status: Office.AsyncResultStatus.Failed,
    value: [],
    error: null,
    asyncContext: undefined,
    diagnostics: undefined,
  } as unknown
}

export function isOfficeAddIn() {
  return (
    isOfficeTaskPane() ||
    window.location.pathname === ROUTES.officeCheckAuth ||
    isEmbeddedAsIFrameInOutlook() ||
    isOfficeDialog()
  )
}

export function isOfficeTaskPane() {
  return window.location.pathname === ROUTES.officeTaskPane
}

function isEmbeddedAsIFrameInOutlook() {
  return (
    window.self !== window.top &&
    (document.referrer.includes('outlook') || // For when the task pane is initially shown in Outlook
      document.referrer.includes(ROUTES.officeTaskPane)) // For when we're redirected to the task pane after login
  )
}

function isOfficeDialog() {
  try {
    return (
      (window.opener && 'Office' in window.opener) ||
      // Can't rely on window.opener for WebView2 (Microsoft Edge2 Chromium-based)
      window.Microsoft?.Office !== undefined ||
      // Sometimes neither window.opener nor window.Microsoft.Office is defined
      // on Outlook for Mac
      (typeof window?.external === 'object' && 'GetHostInfo' in window.external)
    )
  } catch (_error) {
    // To handle cross-origin security issues when trying to access window.opener
    return false
  }
}

export function isOffice(): boolean {
  return (
    typeof Office !== 'undefined' &&
    typeof Office.context !== 'undefined' &&
    typeof Office.context.host !== 'undefined'
  )
}

export function isOfficeUI(): boolean {
  return isOffice() && typeof Office.context.ui !== 'undefined'
}

export function isOfficeParent(): boolean {
  return (
    isOffice() &&
    isOfficeUI() &&
    typeof Office.context.ui.displayDialogAsync !== 'undefined'
  )
}

export function isHostOutlookApplication(): boolean {
  return Office.context.host === Office.HostType.Outlook
}

export function isOfficeWeb(): boolean {
  return Office.context.platform === Office.PlatformType.OfficeOnline
}

export function openOfficeDialog(
  path: string,
  callback?: true | ((asyncResult: Office.AsyncResult<Office.Dialog>) => void),
  options?: Office.DialogOptions,
) {
  const url = window.location.origin + path

  if (!isOfficeUI()) {
    console.error('Office.context.ui is not available, redirecting instead.')
    return window.location.assign(url)
  }

  if (!Office.context.ui.displayDialogAsync) {
    console.error('Office.context.ui.displayDialogAsync is not available.')
    return
  }

  if (callback === true) {
    callback = (asyncResult) => defaultDialog(asyncResult)
  } else if (callback === undefined) {
    callback = () => {
      return
    }
  }

  Office.context.ui.displayDialogAsync(
    url,
    {
      ...options,
      width: widthPixelsToPercentage(options?.width || 1250),
      height: heightPixelsToPercentage(options?.height || 1100),
      promptBeforeOpen: false,
    },
    callback,
  )
}

// Office.js doesn't support pixel widths so we need to convert to percentage
function widthPixelsToPercentage(width: number) {
  return Math.floor((width / window.screen.availWidth) * 100)
}

// Office.js doesn't support pixel heights so we need to convert to percentage
function heightPixelsToPercentage(height: number) {
  return Math.floor((height / window.screen.availHeight) * 100)
}

export function defaultDialog(asyncResult: Office.AsyncResult<Office.Dialog>) {
  const dialog = asyncResult.value
  dialog.addEventHandler(Office.EventType.DialogMessageReceived, async () => {
    dialog.close()
  })
}

export function getToAsyncPromise(): Promise<
  Office.AsyncResult<Office.EmailAddressDetails[]>
> {
  return new Promise((resolve) => {
    if (typeof Office?.context?.mailbox?.item?.to?.getAsync !== 'function') {
      resolve(
        getAsyncResultError() as Office.AsyncResult<
          Office.EmailAddressDetails[]
        >,
      )
      return
    }

    Office?.context?.mailbox?.item?.to.getAsync((result) => {
      resolve(result)
    })
  })
}

export function getCcAsyncPromise(): Promise<
  Office.AsyncResult<Office.EmailAddressDetails[]>
> {
  return new Promise((resolve) => {
    if (typeof Office?.context?.mailbox?.item?.cc?.getAsync !== 'function') {
      resolve(
        getAsyncResultError() as Office.AsyncResult<
          Office.EmailAddressDetails[]
        >,
      )
      return
    }

    Office?.context?.mailbox?.item?.cc.getAsync((result) => {
      resolve(result)
    })
  })
}

export function getBodyAsyncPromise(): Promise<Office.AsyncResult<string>> {
  return new Promise((resolve) => {
    if (typeof Office?.context?.mailbox?.item?.body?.getAsync !== 'function') {
      resolve(getAsyncResultError() as Office.AsyncResult<string>)
      return
    }

    Office?.context?.mailbox?.item?.body.getAsync(
      Office.CoercionType.Text,
      (result) => {
        resolve(result)
      },
    )
  })
}

export function getSubjectAsyncPromise(): Promise<Office.AsyncResult<string>> {
  return new Promise((resolve) => {
    if (
      typeof Office?.context?.mailbox?.item?.subject?.getAsync !== 'function'
    ) {
      resolve(getAsyncResultError() as Office.AsyncResult<string>)
      return
    }

    Office?.context?.mailbox?.item?.subject.getAsync((result) => {
      resolve(result)
    })
  })
}
